import React, { useRef } from 'react'
import cx from 'classnames'
import { useCombinedRefs } from 'hooks'
import { useEntry } from 'intersection-observer'
import { useZinaImage } from 'zina'
import { useDevice } from 'device'

import s from './BackgroundImage.scss'


type BackgroundImageProps = {
  children?: React.ReactNode
  className?: string
  src: string
  lazy?: boolean
  quality?: number
  style?: Record<string, any> // for dev purpose only!
}

const BackgroundImageDefault: React.FC<BackgroundImageProps> = (props) => {
  const { children, className, src, style, quality } = props

  const { isBot } = useDevice()

  const nodeRef = useRef<HTMLDivElement>()
  const { zinaSrc, isLoading } = useZinaImage({ nodeRef, src, quality })
  const finalSrc = isBot ? src : zinaSrc

  const nodeClassName = cx(className, !isBot && isLoading ? s.loader : s.bgImage)
  const backgroundImage = finalSrc ? `url(${finalSrc})` : ''

  return (
    <figure
      ref={nodeRef}
      className={nodeClassName}
      style={{ backgroundImage, ...style }}
    >
      {children}
    </figure>
  )
}

const BackgroundImageLazy: React.FC<BackgroundImageProps> = (props) => {
  const { children, className, src, style, quality } = props

  const [ ref, entry ] = useEntry({ once: true, observerProps: { rootMargin: '25%' } })
  const skip = !entry?.isIntersecting

  const nodeRef = useRef<HTMLDivElement>()
  const { zinaSrc, isLoading } = useZinaImage({ src, nodeRef, skip, quality })

  const nodeClassName = cx(className, isLoading ? s.loader : s.bgImage)
  const backgroundImage = zinaSrc ? `url(${zinaSrc})` : ''

  const finalRef = useCombinedRefs([ ref, nodeRef ])

  return (
    <figure
      ref={finalRef}
      className={nodeClassName}
      style={{ backgroundImage, ...style }}
    >
      {children}
    </figure>
  )
}

const BackgroundImage: React.FunctionComponent<BackgroundImageProps> = (props) => {
  const { lazy } = props
  const { isBot } = useDevice()

  if (isBot || !lazy) {
    return <BackgroundImageDefault {...props} />
  }

  return <BackgroundImageLazy {...props} />
}


export default BackgroundImage
